import isObject from 'lodash-es/isObject';
import camelCase from 'lodash-es/camelCase';

import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/widgets/middleware/theme';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { TopicAttrs, TopicDef, TopicDetails, TopicElement } from 'lcc-state/interfaces';
import { registry } from 'lcc-state';

import { Title } from './title';
import { Image } from './image';
import { ExtendedCSS } from './generator';

import css from '@holmescorp/book-widgets/css/topicpage.m.css';

export interface ItemType {
	[key: string]: TopicElement | string;
}

export interface GenerateItemProperties {
	item: ItemType;
	compconn?: boolean;
	additionalClasses?: ExtendedCSS;
}

const skipElements = new Set(['prolog', 'draft-comment', 'indexterm']);
const possiblySilly = new Set(['p', 'b', 'i']);

const factory = create({ theme }).properties<GenerateItemProperties>();

export const GenerateItem = factory(({ properties, middleware: { theme } }) => {
	const { item, compconn = false, additionalClasses = {} } = properties();
	const themeCss = theme.classes(css);
	let name = Object.keys(item)[0];
	const originalName = name;
	const itemIsArray = Array.isArray(item);
	const itemIsString = typeof item === 'string';
	const itemIsNumber = typeof item === 'number';
	if (itemIsArray || itemIsString || itemIsNumber) {
		name = 'span';
	}
	const value: TopicDetails | string | number = itemIsArray
		? item[0]
		: itemIsString
		? item
		: itemIsNumber
		? `${item}`
		: item[name];
	if (skipElements.has(name) /* || (compconn && name === 'title')*/) {
		return null;
	}

	const classes = [];
	// push through outputclass as is
	const eleitem = item[name] as TopicElement;
	const attrs = eleitem && eleitem.attributes ? (eleitem.attributes as TopicAttrs) : undefined;
	if (attrs && attrs.outputclass) {
		classes.push(attrs.outputclass);
	}

	// sniff out and convert props to attribute
	if (attrs && attrs.props && isObject(value) && isObject(value.attributes)) {
		const splitProps = attrs.props.match(/[a-zA-Z_]+/g);

		if (splitProps && splitProps.length === 2) {
			const newWidget = camelCase(splitProps[1]);
			value.attributes['widget'] = newWidget;

			// override the original widget's name
			name = newWidget;
		}
	}

	// special case styling
	if (name === 'ul') {
		const ulitem = item.ul as TopicElement;
		const attrs = ulitem.attributes as TopicAttrs;
		if (attrs && attrs.outputclass) {
			switch (attrs.outputclass) {
				case 'HCBullet':
					classes.push(themeCss.HCBullet);
					break;
				case 'HCBulletStyle2':
					classes.push(themeCss.HCBulletStyle2);
					break;
				case 'TableUL':
					classes.push(themeCss.TableUL);
					break;
			}
		}
	} else if (name === 'sectiondiv') {
		name = 'section';
	}

	if (name in additionalClasses) {
		classes.push(...additionalClasses[name]);
	}

	if (registry.has(name)) {
		// cast as image to use common types
		if (name === 'title') {
			return w(Title, { title: value as TopicDef, insection: true });
		} else {
			return w<typeof Image>(name, {
				compconn,
				topicdata: value as TopicDef | string,
				additionalClasses: classes,
				originalName
			});
		}
	}
	if (typeof value === 'string') {
		return v(name, { classes }, [value]);
	} else if (typeof value === 'number') {
		return v(name, { classes }, [`${value}`]);
	} else if (typeof value.children === 'string') {
		return v(name, { classes }, [value.children]);
	} else if (typeof value.children === 'number') {
		return v(name, { classes }, [`${value.children}`]);
	} else if (Array.isArray(value.children)) {
		const kids: DNode[] = value.children.map((val: { [key: string]: TopicDetails | string }) => {
			if (typeof val === 'string') {
				return val;
			}
			if (typeof val === 'number') {
				return `${val}`;
			}
			return w(GenerateItem, { item: val as ItemType, compconn });
		});
		return v(name, { classes }, kids);
	} else if (isObject(value)) {
		// some children are simple {ele: string} (e.g li's kids)
		if (possiblySilly.has(name) && value.attributes && !value.children) {
			// silly use of an element with no content
			return null;
		}
		const kids: any = w(GenerateItem, { item: value as ItemType, compconn });
		return v(name, { classes }, Array.isArray(kids) ? kids : [kids]);
	}
	return v(name, { classes }, []);
});
